import * as THREE from 'three';

const material = (color, opacity = 0.8) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });

// These are visual projectiles. The shared counterattack still decides the
// hit, shield, decoy, and damage exactly once, independently of projectile count.
export const createCorePhaseAttacks = (parent) => {
  const group = new THREE.Group();
  group.visible = false;
  parent.add(group);
  const shardGroup = new THREE.Group();
  const pulseGroup = new THREE.Group();
  const arcGroup = new THREE.Group();
  group.add(shardGroup, pulseGroup, arcGroup);

  const shardGeometry = new THREE.OctahedronGeometry(0.18);
  const trailGeometry = new THREE.CylinderGeometry(0.032, 0.006, 1, 5);
  const shards = Array.from({ length: 7 }, () => {
    const head = new THREE.Mesh(shardGeometry, material(0x8cffff));
    const trail = new THREE.Mesh(trailGeometry, material(0x4fe5ff, 0.55));
    shardGroup.add(head, trail);
    return { head, trail };
  });
  const ringGeometry = new THREE.TorusGeometry(1, 0.06, 6, 48);
  const pulses = Array.from({ length: 4 }, () => {
    const ring = new THREE.Mesh(ringGeometry, material(0xf574df));
    const halo = new THREE.Mesh(ringGeometry, material(0xffb3ec, 0.2));
    halo.scale.setScalar(1.12);
    ring.add(halo);
    pulseGroup.add(ring);
    return ring;
  });
  const arcs = Array.from({ length: 3 }, () => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(33 * 2 * 3), 3).setUsage(
        THREE.DynamicDrawUsage
      )
    );
    const indices = [];
    for (let step = 0; step < 32; step += 1) {
      const start = step * 2;
      indices.push(
        start,
        start + 1,
        start + 2,
        start + 1,
        start + 3,
        start + 2
      );
    }
    geometry.setIndex(indices);
    const ribbon = new THREE.Mesh(geometry, material(0xff326d));
    ribbon.frustumCulled = false;
    const head = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.15, 1),
      material(0xffe0d8)
    );
    arcGroup.add(ribbon, head);
    return { ribbon, head };
  });

  const right = new THREE.Vector3();
  const cameraUp = new THREE.Vector3();
  const target = new THREE.Vector3();
  const start = new THREE.Vector3();
  const control = new THREE.Vector3();
  const point = new THREE.Vector3();
  const tail = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const edge = new THREE.Vector3();
  const axis = new THREE.Vector3(0, 1, 0);
  const quadratic = (t, from, through, to, output) =>
    output
      .copy(from)
      .multiplyScalar((1 - t) ** 2)
      .addScaledVector(through, 2 * (1 - t) * t)
      .addScaledVector(to, t * t);
  const segment = (mesh, from, to) => {
    direction.copy(to).sub(from);
    mesh.position.copy(from).lerp(to, 0.5);
    mesh.scale.y = Math.max(0.001, direction.length());
    mesh.quaternion.setFromUnitVectors(axis, direction.normalize());
  };

  const update = ({
    style,
    stage,
    progress,
    origin,
    endpoint,
    camera,
    outcome,
    appearance,
    reduceMotion,
    nearMiss = false,
  }) => {
    const charging = stage === 'bossCharge';
    const firing = stage === 'bossFire';
    const miss = outcome === 'miss';
    const lingering = stage === 'bossImpact' && miss && progress < 0.65;
    group.visible =
      ['shards', 'rings', 'arcs'].includes(style) &&
      (charging || firing || lingering);
    if (!group.visible) return;
    shardGroup.visible = style === 'shards';
    pulseGroup.visible = style === 'rings';
    arcGroup.visible = style === 'arcs';
    const p = THREE.MathUtils.clamp(progress, 0, 1);
    const fade = lingering ? Math.max(0, 1 - p / 0.65) : 1;
    right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);

    if (style === 'shards') {
      shards.forEach(({ head, trail }, index) => {
        const angle = index * 2.399 + (reduceMotion ? 0 : p * 2);
        const spread = 0.35 + (index % 3) * 0.16;
        start
          .copy(origin)
          .addScaledVector(right, Math.cos(angle) * spread)
          .addScaledVector(cameraUp, Math.sin(angle) * spread);
        target.copy(endpoint);
        if (miss && !nearMiss)
          target
            .addScaledVector(right, 0.15 + index * 0.12)
            .addScaledVector(cameraUp, (index - 3) * 0.2);
        const travel = charging
          ? 0
          : lingering
          ? 1 + p * 1.5
          : THREE.MathUtils.clamp((p - index * 0.045) / 0.7, 0, 1);
        head.position.lerpVectors(start, target, travel);
        head.quaternion.copy(camera.quaternion);
        head.rotateZ(angle);
        head.scale.set(0.8, charging ? 1.4 + p : 2.8, 0.8);
        head.material.color.setHex(
          index % 2 ? appearance.hot : appearance.attack
        );
        head.material.opacity = fade * (charging ? 0.3 + p * 0.6 : 0.95);
        trail.visible = !charging && !reduceMotion;
        if (trail.visible) {
          tail.lerpVectors(start, target, Math.max(0, travel - 0.14));
          segment(trail, tail, head.position);
          trail.material.color.setHex(appearance.accent);
          trail.material.opacity = fade * 0.55;
        }
      });
    } else if (style === 'rings') {
      pulses.forEach((ring, index) => {
        target.copy(endpoint);
        if (miss && !nearMiss)
          target
            .addScaledVector(right, index * 0.18)
            .addScaledVector(cameraUp, Math.sin(index * 1.8) * 0.35);
        const travel = charging
          ? 0
          : lingering
          ? 1 + p * 1.5
          : THREE.MathUtils.clamp((p - index * 0.08) / 0.7, 0, 1);
        ring.position.lerpVectors(origin, target, travel);
        ring.quaternion.copy(camera.quaternion);
        const radius = charging
          ? 0.23 + index * 0.14 + (reduceMotion ? 0 : Math.sin(p * 6) * 0.035)
          : 0.13 + travel * 0.16 + index * 0.025;
        ring.scale.setScalar(radius * 1.7);
        ring.material.color.setHex(
          index % 2 ? appearance.hot : appearance.attack
        );
        ring.children[0].material.color.setHex(appearance.accent);
        ring.material.opacity = fade * (charging ? 0.2 + p * 0.5 : 0.85);
        ring.children[0].material.opacity = fade * 0.24;
      });
    } else {
      arcs.forEach(({ ribbon, head }, index) => {
        const angle = index * ((Math.PI * 2) / 3) - Math.PI / 2;
        target.copy(endpoint);
        if (miss && !nearMiss)
          target
            .addScaledVector(right, 0.4 + index * 0.2)
            .addScaledVector(cameraUp, (index - 1) * 0.65);
        control
          .copy(origin)
          .lerp(target, 0.52)
          .addScaledVector(right, Math.cos(angle) * 1.9)
          .addScaledVector(cameraUp, Math.sin(angle) * 1.5);
        const travel = lingering ? 1 + p : THREE.MathUtils.clamp(p / 0.9, 0, 1);
        const positions = ribbon.geometry.attributes.position;
        edge
          .copy(right)
          .multiplyScalar(Math.cos(angle))
          .addScaledVector(cameraUp, Math.sin(angle));
        for (let step = 0; step <= 32; step += 1) {
          if (charging) {
            const orbit =
              angle + (step / 32) * Math.PI * 1.2 + (reduceMotion ? 0 : p * 3);
            point
              .copy(origin)
              .addScaledVector(right, Math.cos(orbit) * (0.4 + p * 0.25))
              .addScaledVector(cameraUp, Math.sin(orbit) * (0.4 + p * 0.25));
          } else {
            const t =
              Math.max(0, travel - 0.38) + (step / 32) * Math.min(0.38, travel);
            quadratic(t, origin, control, target, point);
          }
          const width = reduceMotion
            ? 0.028
            : 0.036 + Math.sin((step / 32) * Math.PI) * 0.032;
          positions.setXYZ(
            step * 2,
            point.x - edge.x * width,
            point.y - edge.y * width,
            point.z - edge.z * width
          );
          positions.setXYZ(
            step * 2 + 1,
            point.x + edge.x * width,
            point.y + edge.y * width,
            point.z + edge.z * width
          );
        }
        positions.needsUpdate = true;
        ribbon.material.color.setHex(
          index === 1 ? appearance.hot : appearance.attack
        );
        ribbon.material.opacity = fade * (charging ? 0.25 + p * 0.5 : 0.9);
        head.visible = !charging;
        quadratic(travel, origin, control, target, head.position);
        head.material.color.setHex(appearance.hot);
        head.material.opacity = fade * 0.85;
      });
    }
  };
  return { group, shards, pulses, arcs, update };
};
